"use client";

import { c as _c } from "react/compiler-runtime";
import dynamic from "next/dynamic";
import posthog from "posthog-js";
import { AuthCtaLink } from "./auth-cta-link";
import { FREE_TIER_ENABLED } from "@/config/feature-flags";
import { FREE_MONTHLY_CONTACT_CAP } from "@/config/stripe-plans";
import type { HeroConfig } from "@/config/counties/types";

// ONLY the demo is client-only. It fabricates rows with Math.random() on every
// render, so server-rendering it guarantees a hydration mismatch — that is why
// the whole hero used to be mounted with `ssr: false`. The cost of that was
// severe: the badge, H1 and subtitle are plain config strings, and excluding
// them from SSR left the homepage with NO <h1> in the server HTML and an LCP of
// 7.2s (server responds in 10ms — the delay was entirely waiting on JS).
// Splitting the boundary here keeps the demo's randomness client-side while the
// headline ships in the initial HTML for crawlers and AI answer engines.
const HeroAiDemo = dynamic(() => import("@/components/landing/hero-ai-demo").then(m => m.HeroAiDemo), {
  ssr: false,
  // Fixed height matching the demo so swapping it in causes no layout shift
  // (CLS is currently 0.001 — keep it there).
  loading: () => <div className="h-[380px] w-full max-w-[720px] rounded-[18px] border border-line bg-surface-alt animate-pulse" />
});
type HeroSectionV2Props = {
  data: HeroConfig;
  ctaHref: string;
};
export default function HeroSectionV2(t0) {
  const $ = _c(20);
  const {
    data
  } = t0;
  let t1;
  if ($[0] !== data.badgeText) {
    t1 = <div className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-primary bg-accent-soft rounded-full px-3.5 py-1.5 mb-5">{data.badgeText}</div>;
    $[0] = data.badgeText;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== data.titleLine1) {
    t2 = <span suppressHydrationWarning={true}>{data.titleLine1}</span>;
    $[2] = data.titleLine1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== data.titleLine2) {
    t3 = <span suppressHydrationWarning={true}>{data.titleLine2}</span>;
    $[4] = data.titleLine2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <h1 className="font-bold text-[30px] sm:text-[40px] lg:text-[50px] leading-[1.08] tracking-[-0.02em] text-ink-soft text-center mb-4">{t2}{" "}{t3}</h1>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== data.subtitle) {
    t5 = <p className="text-[15px] sm:text-base text-body leading-relaxed text-center max-w-[560px] mb-6">{data.subtitle}</p>;
    $[9] = data.subtitle;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== data.ctaSubtext || $[12] !== data.ctaText) {
    t6 = FREE_TIER_ENABLED && <div className="flex flex-col items-center gap-2.5 mb-8"><AuthCtaLink href="/dashboard" onClick={_temp} className="inline-flex items-center justify-center h-[48px] px-7 rounded-lg text-[16px] font-medium bg-primary-surface text-primary-foreground hover:bg-primary-surface/90 transition-colors">{data.ctaText}</AuthCtaLink><p className="text-[13px] text-subtle-ink">{data.ctaSubtext}</p></div>;
    $[11] = data.ctaSubtext;
    $[12] = data.ctaText;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <HeroAiDemo />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t1 || $[16] !== t4 || $[17] !== t5 || $[18] !== t6) {
    t8 = <section className="bg-gradient-to-b from-gradient-top to-card border-b border-line-soft"><div className="max-w-[1160px] mx-auto px-4 sm:px-10 pt-24 sm:pt-28 pb-12 min-h-[calc(100vh-64px)] flex flex-col items-center justify-center">{t1}{t4}{t5}{t6}{t7}</div></section>;
    $[15] = t1;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
function _temp() {
  return posthog.capture("hero_free_cta_clicked", {
    cap: FREE_MONTHLY_CONTACT_CAP
  });
}